import { format, parseISO } from "date-fns";
import type { HotelService } from "@/types/hotelService";
import type {
  ServiceDiscountImpactResponse,
  ServicePopularityResponse,
  ServiceReviewsResponse,
  ServiceUsageByDayResponse,
} from "@/types/analytics-services";
import { at } from "../../utils/analyticsI18n";
import type { TopServiceRow } from "./mockData";
import type {
  DailyRevenuePoint,
  DiscountImpactPoint,
  ServiceDetailAnalytics,
  ServiceDetailExtras,
  ServicePopularityMetrics,
} from "./serviceDetailTypes";

function toMajorAmount(valueMinor: number, divisor: number): number {
  if (!divisor) {
    return valueMinor;
  }
  return valueMinor / divisor;
}

export function formatServiceChartDayLabel(dayKey: string): string {
  try {
    return format(parseISO(dayKey), "MMM d");
  } catch {
    return dayKey;
  }
}

function formatServiceReviewDateTime(value: string): string {
  try {
    return format(parseISO(value), "MMM d, yyyy '·' HH:mm");
  } catch {
    return value || "—";
  }
}

function formatOperatingHours(service: {
  availableFromTime?: string | null;
  availableToTime?: string | null;
}): string {
  const from = service.availableFromTime?.trim();
  const to = service.availableToTime?.trim();

  if (from && to) {
    return `${from} – ${to}`;
  }

  return "—";
}

function formatPriceLabel(service: { price?: string | number | null }): string {
  if (service.price == null || service.price === "") {
    return "—";
  }

  const suffix = at("services.detail.perService", "per service");
  return `${service.price} / ${suffix}`;
}

function resolveImageSource(
  photoId: string | null | undefined,
): Pick<ServiceDetailExtras, "heroImageUrl" | "heroPhotoId"> {
  const value = photoId?.trim();

  if (!value) {
    return {};
  }

  if (/^https?:\/\//i.test(value)) {
    return { heroImageUrl: value };
  }

  return { heroPhotoId: value };
}

export function mapHotelServiceToDetailExtras(
  service: HotelService,
): ServiceDetailExtras {
  return {
    operatingHours: formatOperatingHours(service),
    priceLabel: formatPriceLabel(service),
    ...resolveImageSource(service.photoId),
  };
}

export function mapNavigationServiceToExtras(
  service: TopServiceRow,
): ServiceDetailExtras {
  const imageSource = resolveImageSource(service.photoId);

  return {
    operatingHours: formatOperatingHours(service),
    priceLabel: formatPriceLabel(service),
    ...imageSource,
    heroImageUrl: service.imageUrl ?? imageSource.heroImageUrl,
  };
}

export function mapPopularityToMetrics(
  response: ServicePopularityResponse,
): ServicePopularityMetrics {
  const divisor = response.amountDivisor || 100;

  return {
    totalRevenue: toMajorAmount(response.totalRevenue, divisor),
    guestsUsedPercent: response.guestUsedPercent,
    statusCounts: [
      {
        key: "done",
        label: "Done",
        count: response.completedCount,
      },
      {
        key: "canceled",
        label: "Canceled",
        count: response.canceledCount,
      },
      {
        key: "onHold",
        label: "On Hold",
        count: response.onHoldCount,
      },
    ],
  };
}

function findHighlightDay(points: DailyRevenuePoint[]): string | undefined {
  if (points.length === 0) {
    return undefined;
  }

  const peak = points.reduce((best, point) =>
    point.revenue > best.revenue ? point : best,
  );

  return peak.revenue > 0 ? peak.day : undefined;
}

export function computeRevenueTrendCopy(points: DailyRevenuePoint[]): {
  label: string;
  subtext: string;
} {
  if (points.length < 2) {
    return {
      label: at(
        "services.detail.revenueTrendFlat",
        "Revenue stable for the selected period",
      ),
      subtext: at(
        "services.detail.revenueTrendSubtext",
        "Showing net revenue per day for the selected date range",
      ),
    };
  }

  const midpoint = Math.floor(points.length / 2);
  const firstHalf = points
    .slice(0, midpoint)
    .reduce((sum, point) => sum + point.revenue, 0);
  const secondHalf = points
    .slice(midpoint)
    .reduce((sum, point) => sum + point.revenue, 0);

  if (firstHalf === 0 && secondHalf === 0) {
    return {
      label: at(
        "services.detail.revenueTrendFlat",
        "Revenue stable for the selected period",
      ),
      subtext: at(
        "services.detail.revenueTrendSubtext",
        "Showing net revenue per day for the selected date range",
      ),
    };
  }

  const changePercent =
    firstHalf === 0 ? 100 : ((secondHalf - firstHalf) / firstHalf) * 100;
  const rounded = Math.abs(changePercent).toFixed(1);
  const trendingUp = changePercent >= 0;

  return {
    label: trendingUp
      ? at(
          "services.detail.revenueTrendUp",
          "Trending up by {{percent}}% in the second half of the period",
          { percent: rounded },
        )
      : at(
          "services.detail.revenueTrendDown",
          "Trending down by {{percent}}% in the second half of the period",
          { percent: rounded },
        ),
    subtext: at(
      "services.detail.revenueTrendSubtext",
      "Showing net revenue per day for the selected date range",
    ),
  };
}

export function mapUsageByDayToChart(
  response: ServiceUsageByDayResponse,
): Pick<
  ServiceDetailAnalytics,
  | "dailyRevenue"
  | "revenueTrendLabel"
  | "revenueTrendSubtext"
  | "revenueHighlightDay"
> {
  const divisor = response.amountDivisor || 100;
  const dailyRevenue = response.series.map((row) => ({
    day: formatServiceChartDayLabel(row.day),
    revenue: toMajorAmount(row.revenue, divisor),
  }));
  const trend = computeRevenueTrendCopy(dailyRevenue);

  return {
    dailyRevenue,
    revenueTrendLabel: trend.label,
    revenueTrendSubtext: trend.subtext,
    revenueHighlightDay: findHighlightDay(dailyRevenue),
  };
}

export function mapDiscountImpactToChart(
  response: ServiceDiscountImpactResponse,
): Pick<
  ServiceDetailAnalytics,
  "discountImpact" | "discountSavedPercent" | "discountTotalProfit"
> {
  const divisor = response.amountDivisor || 100;
  const discountImpact: DiscountImpactPoint[] = response.series.map((row) => ({
    label: formatServiceChartDayLabel(row.day),
    revenue: toMajorAmount(row.revenue, divisor),
    discount: toMajorAmount(row.discount, divisor),
  }));

  const { revenue, discount, revenueWithoutDiscount } = response.totals;
  const savedPercent =
    revenueWithoutDiscount > 0
      ? Math.round((discount / revenueWithoutDiscount) * 1000) / 10
      : 0;

  return {
    discountImpact,
    discountSavedPercent: savedPercent,
    discountTotalProfit: toMajorAmount(revenue, divisor),
  };
}

export function mapServiceReviewsToDetailReviews(
  response: ServiceReviewsResponse,
): Pick<ServiceDetailAnalytics, "reviewSummary" | "reviews"> {
  return {
    reviewSummary: {
      overallRating: response.summary.overallRating ?? 0,
      positiveNeutralPercent: response.summary.positiveNeutralPercent ?? 0,
      negativePercent: response.summary.negativePercent ?? 0,
      reviewsCount: response.summary.reviewsCount,
    },
    reviews: response.reviews.map((review) => ({
      id: review.id,
      guestName: review.guestName,
      countryCode: review.countryCode ?? "",
      responsibility: review.responsibility,
      feedbackTime: formatServiceReviewDateTime(review.createdAt),
      feedback: review.feedback,
      rating: review.rating,
    })),
  };
}
